import { useLocalize } from '@/utils/localize'
import type { Agreement } from '@/api/config'

interface Props {
  agreement?: Agreement
  agreements?: Agreement[]
  onClose: () => void
  onConfirm?: () => void
  confirmLabel?: string
}

export default function AgreementSheet({ agreement, agreements, onClose, onConfirm, confirmLabel }: Props) {
  const localize = useLocalize()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrolledToBottom, setScrolledToBottom] = useState(false)

  const docs = agreements && agreements.length > 0 ? agreements : agreement ? [agreement] : []
  const headerTitle = docs.length > 0 ? localize(docs[0], 'title') : ''

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (el.scrollHeight <= el.clientHeight + 20) {
      setScrolledToBottom(true)
    }
  }, [])

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 20) {
      setScrolledToBottom(true)
    }
  }

  const handleConfirm = () => {
    if (onConfirm) onConfirm()
    else onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40">
      <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl max-h-[80vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-800">{headerTitle}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto flex-1 px-5 py-4 text-sm text-gray-600 leading-relaxed space-y-3"
        >
          {docs.map((doc, di) => (
            <div key={di}>
              {di > 0 && (
                <>
                  <div className="border-t border-gray-200 my-4" />
                  <p className="font-bold text-gray-900 text-base mb-1">{localize(doc, 'title')}</p>
                </>
              )}
              {doc.sections.map((section, i) => (
                <div key={i}>
                  <p className="font-bold text-gray-800">{localize(section, 'title')}</p>
                  <p className="whitespace-pre-line">{localize(section, 'content')}</p>
                </div>
              ))}
            </div>
          ))}
          <div className="h-4"></div>
        </div>
        {onConfirm && !scrolledToBottom && (
          <p className="text-center text-xs text-gray-400 py-2 flex-shrink-0">
            ↓ 向下滚动阅读完整内容 ↓
          </p>
        )}
        {onConfirm ? (
          <div className="px-5 pt-2 flex-shrink-0" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
            <button
              onClick={handleConfirm}
              disabled={!scrolledToBottom}
              className="w-full h-12 rounded-2xl text-white font-semibold text-sm whitespace-nowrap transition-all"
              style={scrolledToBottom
                ? { background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', cursor: 'pointer' }
                : { background: '#D1D5DB', cursor: 'not-allowed' }
              }
            >
              {confirmLabel ?? '我已阅读并同意'}
            </button>
          </div>
        ) : confirmLabel ? (
          <div className="px-5 flex-shrink-0" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
            <button
              onClick={onClose}
              className="w-full h-12 rounded-2xl text-white font-semibold text-sm cursor-pointer whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              {confirmLabel}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
